import type {
  ConstellationSpec,
  RackingArea,
  SingularStationArea,
  StationaryArea,
} from '@thive/fddm-types';

import {
  ANGLE_EPSILON_STRICT as ANGLE_EPSILON,
  FULL_ROTATION_RADIANS,
  QUARTER_ROTATION_RADIANS,
} from '@/helpers/angleConstants';
import type { Area } from '@/modules/floorplanDesign/types';

type ConstellationArea = SingularStationArea | StationaryArea;
type DominantAxisArea = ConstellationArea | RackingArea;
type DominantAxisRectangle = DominantAxisArea['rectangle'];
type Stacking = ConstellationSpec['stacking'];

const STACKING_AFTER_CLOCKWISE_QUARTER_TURN: Record<Stacking, Stacking> = {
  HORIZONTAL_TOP: 'VERTICAL_RIGHT',
  HORIZONTAL_MIDDLE: 'VERTICAL_MIDDLE',
  HORIZONTAL_BOTTOM: 'VERTICAL_LEFT',
  VERTICAL_LEFT: 'HORIZONTAL_TOP',
  VERTICAL_MIDDLE: 'HORIZONTAL_MIDDLE',
  VERTICAL_RIGHT: 'HORIZONTAL_BOTTOM',
};

const STACKING_AFTER_COUNTERCLOCKWISE_QUARTER_TURN: Record<Stacking, Stacking> = {
  HORIZONTAL_TOP: 'VERTICAL_LEFT',
  HORIZONTAL_MIDDLE: 'VERTICAL_MIDDLE',
  HORIZONTAL_BOTTOM: 'VERTICAL_RIGHT',
  VERTICAL_LEFT: 'HORIZONTAL_BOTTOM',
  VERTICAL_MIDDLE: 'HORIZONTAL_MIDDLE',
  VERTICAL_RIGHT: 'HORIZONTAL_TOP',
};

const isConstellationArea = (area: Area): area is ConstellationArea =>
  area.areaType === 'SINGULAR' || area.areaType === 'STATIONARY';

const supportsDominantAxisCorrection = (area: Area): area is DominantAxisArea =>
  area.areaType === 'SINGULAR' || area.areaType === 'STATIONARY' || area.areaType === 'RACKING';

const isHorizontalStacking = (stacking: Stacking): boolean =>
  stacking === 'HORIZONTAL_TOP' ||
  stacking === 'HORIZONTAL_MIDDLE' ||
  stacking === 'HORIZONTAL_BOTTOM';

const changedFromLengthToWidthDominant = (
  previousArea: DominantAxisArea,
  updatedArea: DominantAxisArea,
): boolean =>
  previousArea.rectangle.length >= previousArea.rectangle.width &&
  updatedArea.rectangle.length < updatedArea.rectangle.width;

const changedFromWidthToLengthDominant = (
  previousArea: DominantAxisArea,
  updatedArea: DominantAxisArea,
): boolean =>
  previousArea.rectangle.width >= previousArea.rectangle.length &&
  updatedArea.rectangle.width < updatedArea.rectangle.length;

const hasDominantAxisChanged = (
  previousArea: DominantAxisArea,
  updatedArea: DominantAxisArea,
): boolean =>
  changedFromLengthToWidthDominant(previousArea, updatedArea) ||
  changedFromWidthToLengthDominant(previousArea, updatedArea);

const normalizeAngleRadians = (angle: number): number => {
  const normalizedAngle = angle % FULL_ROTATION_RADIANS;

  if (normalizedAngle < 0) return normalizedAngle + FULL_ROTATION_RADIANS;

  return normalizedAngle;
};

const normalizeRectangleAngleRadians = (angle: number): number => {
  const normalizedAngle = angle % FULL_ROTATION_RADIANS;

  if (normalizedAngle < 0) return normalizedAngle + FULL_ROTATION_RADIANS;

  return normalizedAngle;
};

const anglesAreEqual = (angleA: number, angleB: number): boolean => {
  const angleDifference = Math.atan2(Math.sin(angleA - angleB), Math.cos(angleA - angleB));

  return Math.abs(angleDifference) <= ANGLE_EPSILON;
};

const hasUnchangedConstellationOrientation = (
  previousArea: ConstellationArea,
  updatedArea: ConstellationArea,
): boolean =>
  previousArea.constellationSpec.stacking === updatedArea.constellationSpec.stacking &&
  anglesAreEqual(previousArea.constellationSpec.angle, updatedArea.constellationSpec.angle);

const hasUnchangedOrientation = (
  previousArea: DominantAxisArea,
  updatedArea: DominantAxisArea,
): boolean => {
  if (!anglesAreEqual(previousArea.rectangle.angle, updatedArea.rectangle.angle)) {
    return false;
  }

  if (isConstellationArea(previousArea) && isConstellationArea(updatedArea)) {
    return hasUnchangedConstellationOrientation(previousArea, updatedArea);
  }

  return true;
};

const swapRectangleAxes = (
  rectangle: DominantAxisRectangle,
  rotation: number,
): DominantAxisRectangle => ({
  ...rectangle,
  width: rectangle.length,
  length: rectangle.width,
  angle: normalizeRectangleAngleRadians(rectangle.angle + rotation),
});

const applyConstellationOrientation = (
  area: ConstellationArea,
  rectangle: DominantAxisRectangle,
  stacking: Stacking,
  constellationAngle: number,
): ConstellationArea => ({
  ...area,
  rectangle,
  constellationSpec: {
    ...area.constellationSpec,
    stacking,
    angle: constellationAngle,
  },
});

const correctConstellationAreaAfterAxisSwap = (
  area: ConstellationArea,
  rectangle: DominantAxisRectangle,
): ConstellationArea => {
  const {
    constellationSpec: { stacking, angle: constellationAngle },
  } = area;

  if (isHorizontalStacking(stacking)) {
    return applyConstellationOrientation(
      area,
      rectangle,
      STACKING_AFTER_CLOCKWISE_QUARTER_TURN[stacking],
      normalizeAngleRadians(constellationAngle - QUARTER_ROTATION_RADIANS),
    );
  }

  return applyConstellationOrientation(
    area,
    rectangle,
    STACKING_AFTER_COUNTERCLOCKWISE_QUARTER_TURN[stacking],
    normalizeAngleRadians(constellationAngle + QUARTER_ROTATION_RADIANS),
  );
};

/**
 * Keeps the area visually aligned when resizing changes its dominant axis.
 * An update that already contains a changed orientation is treated as
 * authoritative. This prevents exact history snapshots from being
 * transformed a second time.
 *
 * Constellation areas also receive corrected stacking and constellation
 * orientation. Racking areas only receive the rectangle axis correction.
 */
export const adjustAreaForDominantAxisChange = (
  previousArea: Area | undefined,
  updatedArea: Area,
): Area => {
  if (!previousArea) return updatedArea;
  if (
    !supportsDominantAxisCorrection(previousArea) ||
    !supportsDominantAxisCorrection(updatedArea)
  ) {
    return updatedArea;
  }
  if (previousArea.areaType !== updatedArea.areaType) return updatedArea;
  if (!hasUnchangedOrientation(previousArea, updatedArea)) return updatedArea;
  if (!hasDominantAxisChanged(previousArea, updatedArea)) return updatedArea;
  if (!isConstellationArea(updatedArea)) {
    return {
      ...updatedArea,
      rectangle: swapRectangleAxes(updatedArea.rectangle, QUARTER_ROTATION_RADIANS),
    };
  }

  const {
    constellationSpec: { stacking },
  } = updatedArea;
  const rectangle = isHorizontalStacking(stacking)
    ? swapRectangleAxes(updatedArea.rectangle, QUARTER_ROTATION_RADIANS)
    : swapRectangleAxes(updatedArea.rectangle, -QUARTER_ROTATION_RADIANS);

  const constellationArea = correctConstellationAreaAfterAxisSwap(updatedArea, rectangle);
  return constellationArea;
};
